import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useReducer, useCallback, useContext, useMemo, useState } from 'react';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton } from '@fluentui/react';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { VerticalSeparatedLayout } from '@bingads-webui-react/steps';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { StepFlowProvider, BasicStepLogger } from '@bingads-webui-campaign-react/step-wizard-logger';
import { useStepFlowEvent } from '@bingads-webui-campaign-react/auto-goal-data-repository';
import { getTags } from './conversion-goals-repository';
import { startActivity, endActivity } from './shared/util';
import { GoalCreationWizardContext } from './goal-creation-wizard-context';
import { goalCreationWizardReducer, WIZARD_ACTIONS } from './goal-creation-wizard-reducer';
import { getStyles } from './get-styles';
import { STEP_PATHS } from './step-paths';
import {
  GoalObjectiveStep,
  GoalTypeStep,
  GoalDetailsStep,
  SmartGoalDetail,
  TagSelectionSubstep,
  TagSetupMethodStep,
  FinishStep,
  TagSetupInstallManuallyStep,
  TagSetupSendToDeveloperStep,
  TagSetupThirdPartyPlatformStep,
  TagSetupThirdPartyInstructionStep,
  TagSetupInstallEventTag,
  GtmBaseTagSetupStep,
  TagSetupInstallEventTagWithClarity
} from './steps/index';
import { getTagTemplates } from './steps/utils/tag-template';
import { TagSetupChoiceSelectionStep } from './steps/tag-setup-step/tag-setup-choice-selection';
import { EnhancedConversions } from './steps/goal-details-step/enhanced-conversions';
import { SetUpEnhancedConversions } from './steps/tag-setup-step/set-up-enhanced-conversions';
import { isTypeIncluedForEnhancedConversions } from './shared/contants';
import { useShowPmaxAutoSwitchMessage } from './steps/finish-step/pmax-auto-switch-message';
import { useShowNetworkCards } from './steps/goal-details-step/use-show-network-cards';

const {
  StepsContextProvider,
  StepContainer,
  Step,
  StepProgressList,
} = VerticalSeparatedLayout;

const GoalCreationWizardUnstyled = (props) => {
  const {
    classes,
    onWizardExit,
    permissions,
    serverContext,
    scope,
    goalData,
    wizardDefaultState,
    perfMarker,
    parentActivity,
    registerBackButtonClickedHandler,
    isDemo,
    hasConversionCouponFunc,
    onCouponAdoptedFunc,
    autoGoalBackHandlerFunc,
    autoGoalSelectedObjective,
  } = props;

  const {
    CurrentAccount: {
      AdvertiserCustomerID: cid,
      Id: aid,
    },
  } = serverContext;

  const tagPromise = useMemo(() => getTags(cid, aid), [cid, aid]);

  const i18n = useContext(I18nContext);
  const [state, dispatch] = useReducer(goalCreationWizardReducer, wizardDefaultState);

  const isUetSpaTrackingEnabled = _.result(permissions.dynamic, 'UetAutoSpaTracking');
  const enableEnhancedConversions = _.result(permissions.dynamic, 'EnhancedConversions');
  const tagTemplates = useMemo(() => getTagTemplates(i18n, isUetSpaTrackingEnabled), [i18n, isUetSpaTrackingEnabled]);
  const logActivity = useMemo(() => {
    const activity = state.logScenario[state.currentStep].create();
    startActivity(activity);
    return activity;
  }, [state.logScenario, state.currentStep]);
  const [showConfirmCancelationDialog, setShowConfirmCancelationDialog] = useState(false);
  const showPmaxAutoSwitchMessage = useShowPmaxAutoSwitchMessage(aid, cid);
  const showNetworkCards = useShowNetworkCards({ cid, aid, permissions });

  const goToNext = useCallback(() => {
    endActivity(logActivity);
    dispatch({ type: WIZARD_ACTIONS.WIZARD_NAVIGATE_NEXT });
  }, [logActivity]);

  const goBack = useCallback(() => {
    endActivity(logActivity);
    dispatch({ type: WIZARD_ACTIONS.WIZARD_NAVIGATE_BACK });
  }, [logActivity]);

  const wizardContext = useMemo(() => ({
    goToNext,
    goBack,
    dispatch,
    permissions,
    serverContext,
    tagTemplates,
    startFromEdit: state.startFromEdit,
    isEditScenario: state.isEditScenario,
    selectedObjective: state.selectedObjective,
    deleteClarityEvent: state.deleteClarityEvent,
    tagInstallOption: state.tagInstallOption,
    hasConversionCouponFunc,
    onCouponAdoptedFunc,
    tagPromise,
    goalData,
    goalContext: {
      ...state.goalObject, // overwrite read-from
    },
    GTMContext: state.GTMContext,
    associatedCampaigns: state.associatedCampaigns,
    logActivity,
    scope,
    cid,
    aid,
    isDemo,
  }), [goToNext, goBack, permissions, serverContext, tagTemplates, state.startFromEdit, state.isEditScenario, state.selectedObjective, state.deleteClarityEvent, state.tagInstallOption, state.goalObject, state.GTMContext, state.associatedCampaigns, hasConversionCouponFunc, onCouponAdoptedFunc, tagPromise, goalData, logActivity, scope, cid, aid, isDemo]);

  const logSuccess = useCallback(() => {
    setClarityTag({ IsConversionGoalWorkflowSuccess: true });
    const logObject = {
      isEditScenario: wizardContext.startFromEdit,
      goalId: wizardContext.goalContext.Id,
      goalContext: _.omit(wizardContext.goalContext, 'tags'),
      currentStep: state.currentStep,
      isComplete: true,
      isSaveDone: !!state.isSaveDone,
      stepHistory: state.loggingSteps,
      tagCount: wizardContext.goalContext && wizardContext.goalContext.tags ? wizardContext.goalContext.tags.length : 0,
    };
    parentActivity.trace(`Wizard workflow complete log: ${JSON.stringify(logObject)}`);
  }, [parentActivity, state.currentStep, state.isSaveDone, state.loggingSteps, wizardContext.goalContext, wizardContext.startFromEdit]);

  const logFailure = useCallback((...args) => {
    setClarityTag({ IsConversionGoalWorkflowSuccess: false });
    const logObject = {
      isEditScenario: wizardContext.startFromEdit,
      goalContext: _.omit(wizardContext.goalContext, 'tags'),
      currentStep: args.length > 0 ? `${state.currentStep}/${args[0]}` : state.currentStep, // concat step/platform
      isComplete: false,
      isSaveDone: !!state.isSaveDone,
      stepHistory: state.loggingSteps,
      tagCount: wizardContext.goalContext && wizardContext.goalContext.tags ? wizardContext.goalContext.tags.length : 0,
    };
    parentActivity.trace(`Wizard workflow incomplete log: ${JSON.stringify(logObject)}`);
  }, [parentActivity, state.currentStep, state.isSaveDone, state.loggingSteps, wizardContext.goalContext, wizardContext.startFromEdit]);

  wizardContext.logGoalSaved = useCallback((goal) => {
    setClarityTag({ IsConversionGoalSaved: true });
    parentActivity.trace(`Conversion goal saved with isEditScenario=${wizardContext.startFromEdit}: ${JSON.stringify(goal)}`);
  }, [parentActivity, wizardContext.startFromEdit]);

  registerBackButtonClickedHandler(() => {
    logFailure();
  });

  wizardContext.exitWizard = useCallback((...args) => {
    endActivity(logActivity);

    const isDoneClicked = args.length > 1 && args[1] === true;
    setClarityTag({ IsConversionGoalWorkflowSuccess: isDoneClicked.toString() });

    if (isDoneClicked) {
      logSuccess();
    } else {
      logFailure(...args);
    }
    endActivity(parentActivity);
    onWizardExit(args.length > 1 ? args[0] : undefined); // args.length > 1 should only apply to finish step
  }, [logActivity, parentActivity, onWizardExit, logSuccess, logFailure]);

  const cancelConfirmed = useCallback(() => {
    wizardContext.exitWizard();
  }, [wizardContext]);

  wizardContext.cancel = useCallback(() => {
    if (hasConversionCouponFunc && hasConversionCouponFunc()) {
      setShowConfirmCancelationDialog(true);
    } else {
      cancelConfirmed();
    }
  }, [hasConversionCouponFunc, cancelConfirmed]);

  const getStepProgressItemGlyphicon = useCallback((step) => {
    if (step.path.indexOf('.') >= 0) {
      return 'ba-RadioBullet';
    }

    if (step.isSelected || step.containsSelected) {
      return 'ba-RadioBullet2';
    } else if (step.isBehind) {
      return 'ba-CompletedSolid';
    }

    return 'ba-RadioBtnOff';
  }, []);

  const dialogContentProps = {
    type: DialogType.normal,
    title: i18n.getString(_TL_('Are you sure you want to cancel?')),
  };

  const childPerfMarker = useMemo(() => perfMarker.createChild(state.currentStep), [state.currentStep, perfMarker]);

  const continueGoalCreation = useCallback(() => {
    setShowConfirmCancelationDialog(false);
  }, []);

  const supportedTypeForEnhancedConv = useMemo(
    () => {
      if (_.isEmpty(wizardContext.goalContext)) {
        return true;
      }
      const type = wizardContext.goalContext.Type;
      const valueType = wizardContext.goalContext.GoalValueType;
      return isTypeIncluedForEnhancedConversions({ goalType: type, goalValueType: valueType });
    },
    [wizardContext.goalContext]
  );

  return (
    <StepFlowProvider
      postEventLog={useStepFlowEvent}
    >
      <BasicStepLogger
        flow={autoGoalBackHandlerFunc ? 'AutoGoalManualWizard' : 'ManualGoalWizard'}
        subflow={state.isEditScenario ? 'edit' : 'creation'}
        Page={state.currentStep}
      />
      <Dialog
        hidden={!showConfirmCancelationDialog}
        onDismiss={continueGoalCreation}
        dialogContentProps={dialogContentProps}
        minWidth={500}
      >
        <div>
          <ReactTemplate
            model={{
              br: (<br />),
              b: text => (<b>{text}</b>),
            }}
            template={i18n.getString(_TL_('To unlock this limited-time offer, you must set up at least one conversion goal.{{br}}Click {{b}}Cancel{{/b}} and return to complete your import.'))}
          />
        </div>
        <DialogFooter>
          <PrimaryButton onClick={continueGoalCreation} text={i18n.getString(_TL_('Continue creating this goal'))} />
          <DefaultButton onClick={cancelConfirmed} text={i18n.getString(_TL_('Cancel goal setup'))} />
        </DialogFooter>
      </Dialog>
      <div className={classNames('goal-creation-wizard', 'goal-creation-page', classes.conversionWizard)}>
        <FluentProvider theme={webLightTheme} style={{ height: '100%' }}>
          <GoalCreationWizardContext.Provider value={wizardContext}>
            <StepsContextProvider currentPath={state.currentStep}>
              <div id="goal-creation-wizard-steps-content" className={classes.wizardContentContainer}>
                <div id="goal-creation-wizard-steps-list" className={classes.wizardListContainer}>
                  <StepProgressList getItemGlyphicon={getStepProgressItemGlyphicon} />
                </div>
                <StepContainer>
                  <Step
                    title={i18n.getString(_TL_('Conversion goal'))}
                    path={STEP_PATHS.goalObjective.id}
                  >
                    <GoalObjectiveStep perfMarker={childPerfMarker} autoGoalBackHandlerFunc={autoGoalBackHandlerFunc} />
                  </Step>
                  <Step
                    title={i18n.getString(_TL_('Conversion goal type'))}
                    path={STEP_PATHS.goalType.id}
                  >
                    <GoalTypeStep
                      selectedObjective={state.selectedObjective}
                      permissions={permissions}
                      perfMarker={childPerfMarker}
                      autoGoalBackHandlerFunc={autoGoalBackHandlerFunc}
                      autoGoalSelectedObjective={autoGoalSelectedObjective}
                    />
                  </Step>
                  <Step
                    title={i18n.getString(_TL_('Conversion goal details'))}
                    path={STEP_PATHS.goalDetails.id}
                  >
                    <StepContainer>
                      <Step
                        title={enableEnhancedConversions ? i18n.getString(_TL_('Edit goal')) : undefined}
                        path={STEP_PATHS.goalDetails.normalGoal.id}
                      >
                        <GoalDetailsStep
                          stepTitle={i18n.getString(_TL_('Create a goal'))}
                          perfMarker={childPerfMarker}
                          showNetworkCards={showNetworkCards}
                        />
                      </Step>
                      {enableEnhancedConversions && supportedTypeForEnhancedConv
                        &&
                        <Step
                          title={i18n.getString(_TL_('Enhanced conversions'))}
                          path={STEP_PATHS.goalDetails.enhancedConversions.id}
                        >
                          <EnhancedConversions
                            stepTitle={i18n.getString(_TL_('Enhanced Conversions'))}
                            perfMarker={childPerfMarker}
                          />
                        </Step>
                      }
                      <Step path={STEP_PATHS.goalDetails.smartGoal.id}>
                        <SmartGoalDetail perfMarker={childPerfMarker} />
                      </Step>
                    </StepContainer>
                  </Step>
                  <Step path={STEP_PATHS.tagSetup.id} title={i18n.getString(_TL_('Set up tagging'))}>
                    <StepContainer subContainer>
                      <Step path={STEP_PATHS.tagSetup.selectTag.id}>
                        <TagSelectionSubstep perfMarker={childPerfMarker} />
                      </Step>
                      <Step path={STEP_PATHS.tagSetup.noTag.id}>
                        <StepContainer subContainer>
                          <Step
                            path={STEP_PATHS.tagSetup.noTag.selectTagMethod.id}
                          >
                            <TagSetupMethodStep
                              perfMarker={childPerfMarker}
                              selectedItem={state.selectedTagSetupMethod}
                            />
                          </Step>
                          <Step
                            path={STEP_PATHS.tagSetup.noTag.installTag.id}
                          >
                            <TagSetupInstallManuallyStep perfMarker={childPerfMarker} permissions={permissions} />
                          </Step>
                          <Step
                            path={STEP_PATHS.tagSetup.noTag.emailTag.id}
                          >
                            <TagSetupSendToDeveloperStep perfMarker={childPerfMarker} />
                          </Step>
                          <Step
                            path={STEP_PATHS.tagSetup.noTag.platformTag.id}
                          >
                            <TagSetupThirdPartyPlatformStep perfMarker={childPerfMarker} />
                          </Step>
                          <Step
                            path={STEP_PATHS.tagSetup.noTag.platformTagInstructions.id}
                          >
                            <TagSetupThirdPartyInstructionStep
                              selectedPlatform={state.selectedPlatform}
                              perfMarker={childPerfMarker}
                            />
                          </Step>
                          <Step
                            path={STEP_PATHS.tagSetup.noTag.gtmTag.id}
                          >
                            <GtmBaseTagSetupStep
                              selectedPlatform="GoogleTagManager"
                              serverContext={serverContext}
                              onWorkflowExit={wizardContext.exitWizard}
                            />
                          </Step>
                        </StepContainer>
                      </Step>
                      <Step path={STEP_PATHS.tagSetup.addEventTag.id}>
                        <TagSetupInstallEventTag perfMarker={childPerfMarker} permissions={permissions} />
                      </Step>
                      <Step path={STEP_PATHS.tagSetup.addEventTagWithClarity.id}>
                        <TagSetupInstallEventTagWithClarity perfMarker={childPerfMarker} />
                      </Step>
                      <Step path={STEP_PATHS.tagSetup.eventTagChoice.id}>
                        <TagSetupChoiceSelectionStep perfMarker={childPerfMarker} permissions={permissions} />
                      </Step>
                      <Step path={STEP_PATHS.tagSetup.setupEnhancedConv.id}>
                        <SetUpEnhancedConversions perfMarker={childPerfMarker} permissions={permissions} />
                      </Step>
                    </StepContainer>
                  </Step>
                  <Step
                    title={i18n.getString(_TL_('All done!'))}
                    path={STEP_PATHS.endFlow.id}
                  >
                    <FinishStep
                      perfMarker={childPerfMarker}
                      showPmaxAutoSwitchMessage={showPmaxAutoSwitchMessage}
                    />
                  </Step>
                </StepContainer>
                <iframe title="logoutIFrame" name="logoutIFrame" style={{ display: 'none' }} />
              </div>
            </StepsContextProvider>
          </GoalCreationWizardContext.Provider>
        </FluentProvider>
      </div>
    </StepFlowProvider>
  );
};

GoalCreationWizardUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(getStyles(), _.constant(PropTypes.string.isRequired)))
    .isRequired,
  onWizardExit: PropTypes.func.isRequired,
  permissions: PropTypes.shape({ dynamic: { UetAutoSpaTracking: PropTypes.bool } }).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({}),
  }).isRequired,
  goalData: PropTypes.shape({}).isRequired,
  wizardDefaultState: PropTypes.shape({}).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  perfMarker: PropTypes.shape({
    done: PropTypes.func,
    willInit: PropTypes.func,
    willRender: PropTypes.func,
    createChild: PropTypes.func,
  }).isRequired,
  parentActivity: PropTypes.shape({}).isRequired,
  registerBackButtonClickedHandler: PropTypes.func.isRequired,
  isDemo: PropTypes.bool.isRequired,
  hasConversionCouponFunc: PropTypes.func.isRequired,
  onCouponAdoptedFunc: PropTypes.func.isRequired,
  autoGoalBackHandlerFunc: PropTypes.func,
};

GoalCreationWizardUnstyled.defaultProps = {
  autoGoalBackHandlerFunc: null,
};

export const GoalCreationWizard = withPerfMarker(withInstrumentation(withDefaultStyles(GoalCreationWizardUnstyled, getStyles)));
